
import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function DX() {
  const [activeTab, setActiveTab] = useState('online');

  const onlineContent = [
    {
      title: 'オンライン服薬指導',
      description: 'ビデオ通話による遠隔服薬指導',
      features: ['ビデオ通話システム', '電子薬歴連携', '処方箋確認', '服薬指導記録'],
      icon: 'ri-video-line',
      color: 'bg-blue-50 text-blue-600'
    },
    {
      title: 'オンライン健康相談',
      description: '薬剤師による健康相談サービス',
      features: ['健康相談対応', 'OTC薬選択支援', '生活指導', 'フォローアップ'],
      icon: 'ri-chat-heart-line',
      color: 'bg-green-50 text-green-600'
    },
    {
      title: 'デジタル薬歴管理',
      description: 'クラウド型薬歴管理システム',
      features: ['リアルタイム更新', '多店舗連携', 'データ分析', 'セキュリティ強化'],
      icon: 'ri-file-cloud-line',
      color: 'bg-purple-50 text-purple-600'
    }
  ];

  const prescriptionContent = [
    {
      title: '電子処方箋システム',
      description: '処方箋の電子化とデジタル処理',
      features: ['QRコード読取', '自動入力', 'リアルタイム連携', 'ペーパーレス化'],
      icon: 'ri-qr-code-line',
      color: 'bg-teal-50 text-teal-600'
    },
    {
      title: '処方箋事前送信',
      description: '患者による処方箋の事前送信',
      features: ['アプリ連携', '待ち時間短縮', '調剤準備', '来局通知'],
      icon: 'ri-smartphone-line',
      color: 'bg-orange-50 text-orange-600'
    },
    {
      title: 'AI処方監査',
      description: 'AIによる処方内容の自動チェック',
      features: ['相互作用チェック', '用法用量確認', 'アレルギー確認', '重複投薬チェック'],
      icon: 'ri-robot-line',
      color: 'bg-red-50 text-red-600'
    }
  ];

  const automationContent = [
    {
      title: 'お薬ロッカー',
      description: '24時間対応の薬剤受け渡しシステム',
      features: ['24時間対応', 'セキュリティ機能', '温度管理', '受取通知'],
      icon: 'ri-safe-line',
      color: 'bg-indigo-50 text-indigo-600'
    },
    {
      title: '自動調剤システム',
      description: 'ロボットによる自動調剤',
      features: ['高精度調剤', '効率化', '人的ミス削減', '在庫管理'],
      icon: 'ri-robot-2-line',
      color: 'bg-cyan-50 text-cyan-600'
    },
    {
      title: 'IoT在庫管理',
      description: 'IoTセンサーによる在庫の自動管理',
      features: ['リアルタイム監視', '自動発注', '期限管理', 'データ分析'],
      icon: 'ri-database-2-line',
      color: 'bg-pink-50 text-pink-600'
    }
  ];

  const getContentByTab = (tab: string) => {
    switch (tab) {
      case 'online': return onlineContent;
      case 'prescription': return prescriptionContent;
      case 'automation': return automationContent;
      default: return onlineContent;
    }
  };

  const currentContent = getContentByTab(activeTab);

  const implementationSteps = [
    {
      step: '1',
      title: '現状分析',
      description: '現在の業務フローと課題の洗い出し',
      details: ['業務プロセス分析', '課題特定', 'ニーズ調査', 'ROI試算']
    },
    {
      step: '2',
      title: 'システム選定',
      description: '薬局に最適なDXソリューションの選択',
      details: ['要件定義', 'ベンダー比較', 'デモ・トライアル', '導入計画策定']
    },
    {
      step: '3',
      title: '導入・設定',
      description: 'システムの導入と初期設定',
      details: ['システム構築', 'データ移行', '動作確認', 'セキュリティ設定']
    },
    {
      step: '4',
      title: '研修・運用',
      description: 'スタッフ研修と本格運用開始',
      details: ['操作研修', '運用ルール策定', '本格稼働', '効果測定']
    }
  ];

  const benefits = [
    {
      title: '業務効率化',
      description: '自動化により業務時間を大幅短縮',
      icon: 'ri-time-line',
      metrics: ['調剤時間50%短縮', '事務作業70%削減', '待ち時間30%短縮']
    },
    {
      title: '医療安全向上',
      description: 'AIチェックによりミスを大幅削減',
      icon: 'ri-shield-check-line',
      metrics: ['調剤ミス90%削減', '相互作用チェック100%', '重複投薬防止']
    },
    {
      title: '患者満足度向上',
      description: '利便性向上により患者満足度アップ',
      icon: 'ri-customer-service-line',
      metrics: ['待ち時間短縮', '24時間対応', 'オンライン相談']
    },
    {
      title: '収益性改善',
      description: '効率化により収益性を向上',
      icon: 'ri-line-chart-line',
      metrics: ['人件費削減', '処方箋枚数増加', '新サービス展開']
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <div className="w-10 h-10 mr-3 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg transform rotate-12 shadow-lg"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-lg"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-600" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </Link>
            </div>
            <nav className="hidden md:flex space-x-8">
              <Link to="/services" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">サービス</Link>
              <Link to="/health-support" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">健康サポート</Link>
              <Link to="/pharmacist-resources" className="text-teal-600 hover:text-teal-700 font-medium whitespace-nowrap cursor-pointer">薬剤師向け</Link>
              <Link to="/information" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">情報提供</Link>
            </nav>
            <button className="md:hidden p-2">
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-indigo-50 to-purple-50 py-16">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/10ceb2bfff842ed9a27b5c0ca7df5184.jpg"
            alt="薬局DX"
            className="w-full h-full object-cover object-top opacity-20"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            薬局<span className="text-indigo-600">DX</span>推進
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            デジタル技術で薬局業務を革新し、患者サービスを向上
          </p>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 justify-center">
            <button
              onClick={() => setActiveTab('online')}
              className={`px-6 py-3 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'online'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-indigo-50 hover:text-indigo-600'
              }`}
            >
              オンラインサービス
            </button>
            <button
              onClick={() => setActiveTab('prescription')}
              className={`px-6 py-3 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'prescription'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-indigo-50 hover:text-indigo-600'
              }`}
            >
              電子処方箋
            </button>
            <button
              onClick={() => setActiveTab('automation')}
              className={`px-6 py-3 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'automation'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-indigo-50 hover:text-indigo-600'
              }`}
            >
              自動化・IoT
            </button>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {currentContent.map((item, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow p-8 border border-gray-100">
                <div className={`w-16 h-16 rounded-xl ${item.color} flex items-center justify-center mb-6`}>
                  <i className={`${item.icon} text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{item.title}</h3>
                <p className="text-gray-600 mb-6">{item.description}</p>
                <ul className="space-y-2">
                  {item.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <i className="ri-check-line text-indigo-600 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation Steps */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">DX導入ステップ</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {implementationSteps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                  <div className="w-12 h-12 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xl font-bold mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600 mb-4">{step.description}</p>
                  <ul className="space-y-1">
                    {step.details.map((detail, idx) => (
                      <li key={idx} className="text-sm text-gray-500">• {detail}</li>
                    ))}
                  </ul>
                </div>
                {index < implementationSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <i className="ri-arrow-right-line text-2xl text-gray-300"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">DX導入効果</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 text-center">
                <div className="w-16 h-16 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${benefit.icon} text-2xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 mb-4">{benefit.description}</p>
                <ul className="space-y-1">
                  {benefit.metrics.map((metric, idx) => (
                    <li key={idx} className="text-sm text-indigo-600 font-medium">✓ {metric}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Trends */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">最新技術トレンド</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-8">
              <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-brain-line text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">AI・機械学習</h3>
              <p className="text-gray-600 mb-6">
                AIを活用した処方監査、在庫予測、患者データ分析により、より精度の高い薬剤師業務を実現
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-center">
                  <i className="ri-check-line text-blue-600 mr-2"></i>
                  処方内容の自動チェック
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-blue-600 mr-2"></i>
                  在庫需要予測
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-blue-600 mr-2"></i>
                  患者行動分析
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-8">
              <div className="w-16 h-16 bg-purple-100 text-purple-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-cloud-line text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">クラウド・API連携</h3>
              <p className="text-gray-600 mb-6">
                クラウドベースのシステムとAPI連携により、シームレスな情報共有と業務効率化を実現
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-center">
                  <i className="ri-check-line text-purple-600 mr-2"></i>
                  多店舗データ連携
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-purple-600 mr-2"></i>
                  医療機関との連携
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-purple-600 mr-2"></i>
                  リアルタイム情報共有
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            薬局DXについてのご相談
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            デジタル化の導入や運営についてお気軽にご相談ください
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
              className="bg-indigo-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-indigo-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              DX相談
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 mr-2 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg transform rotate-12"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-300 to-emerald-300 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-sm"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-400" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </div>
              <p className="text-gray-400">
                患者さんと薬剤師をつなぐ総合医療情報プラットフォーム
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">薬局DX</h3>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => setActiveTab('online')} className="hover:text-white cursor-pointer">オンラインサービス</button></li>
                <li><button onClick={() => setActiveTab('prescription')} className="hover:text-white cursor-pointer">電子処方箋</button></li>
                <li><button onClick={() => setActiveTab('automation')} className="hover:text-white cursor-pointer">自動化・IoT</button></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">薬剤師向け</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/pharmacist-resources" className="hover:text-white cursor-pointer">専門リソース</Link></li>
                <li><Link to="/specialty-pharmacy" className="hover:text-white cursor-pointer">専門薬局</Link></li>
                <li><Link to="/sterile-preparation" className="hover:text-white cursor-pointer">無菌調剤</Link></li>
                <li><Link to="/home-care" className="hover:text-white cursor-pointer">在宅医療</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">情報・リンク</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/information" className="hover:text-white cursor-pointer">公的機関リンク</Link></li>
                <li><Link to="/guidelines" className="hover:text-white cursor-pointer">疾患ガイドライン</Link></li>
                <li><Link to="/career" className="hover:text-white cursor-pointer">転職情報</Link></li>
                <li><Link to="/entertainment" className="hover:text-white cursor-pointer">エンタメ</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 みんなの薬局～Pharmacare～. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}